import React, { useState, useEffect } from 'react';
import { abacUserService } from '../../services/abac';
import { abacUserAttributeService } from '../../services/abac';
import { toast } from 'react-hot-toast';
import { Modal } from '../../components/ui';
import { useLanguage } from '../../state/language/LanguageProvider';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  custom_attributes: Array<{
    attribute_name: string;
    value: any;
  }>;
}

interface Attribute {
  id: string;
  attribute_name: string;
  data_type: string;
  description: string;
}

const UserAttributeAssignmentPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<Attribute | null>(null);
  const [attributeValue, setAttributeValue] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch users and attributes separately to handle individual failures
      try {
        const usersResponse = await abacUserService.getAll();
        setUsers(usersResponse.data || []);
      } catch (error) {
        toast.error(t('abac.userAttributeAssignment.failedToFetchUsers'));
        setUsers([]);
      }
      
      try {
        const attributesResponse = await abacUserAttributeService.getAll();
        setAttributes(attributesResponse.data || []);
      } catch (error) {
        toast.error(t('abac.userAttributeAssignment.failedToFetchAttributes'));
        setAttributes([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('abac.userAttributeAssignment.failedToFetchData'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAttribute = (user: User) => {
    setSelectedUser(user);
    setShowAssignmentModal(true);
  };

  const handleSaveAssignment = async () => {
    if (!selectedUser || !selectedAttribute || !attributeValue.trim()) {
      toast.error(t('abac.userAttributeAssignment.pleaseFillAllFields'));
      return;
    }

    try {
      await abacUserService.assignAttributeValues({
        entity_type: 'USER',
        entity_id: selectedUser.id,
        attribute_id: selectedAttribute.id,
        values: [attributeValue]
      });
      
      toast.success(t('abac.userAttributeAssignment.attributeValueAssignedSuccessfully'));
      setShowAssignmentModal(false);
      setSelectedUser(null);
      setSelectedAttribute(null);
      setAttributeValue('');
      fetchData(); // Refresh the data
    } catch (error) {
      toast.error(t('abac.userAttributeAssignment.failedToAssignAttributeValue'));
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('abac.userAttributeAssignment.loadingUsers')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('abac.userAttributeAssignment.title')}</h1>
        <p className="text-gray-600 mt-2">
          {t('abac.userAttributeAssignment.description')}
        </p>
      </div>



      {/* Simple User List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold">{t('abac.userAttributeAssignment.users')} ({users.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('abac.fieldLabels.email')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('abac.fieldLabels.name')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('abac.fieldLabels.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('abac.fieldLabels.attributes')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    {t('abac.userAttributeAssignment.noUsersFound')}
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.status}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.custom_attributes?.length || 0} {t('abac.userAttributeAssignment.attributes')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleAssignAttribute(user)}
                        className="text-blue-600 hover:text-blue-900 mr-2"
                      >
                        {t('abac.userAttributeAssignment.assignAttribute')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Attribute Assignment Modal */}
      <Modal
        isOpen={showAssignmentModal}
        onClose={() => {
          setShowAssignmentModal(false);
          setSelectedUser(null);
          setSelectedAttribute(null);
          setAttributeValue('');
        }}
        title={t('abac.userAttributeAssignment.assignAttributeTo', { firstName: selectedUser?.first_name || 'User', lastName: selectedUser?.last_name || '' })}
        size="lg"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('abac.userAttributeAssignment.selectAttribute')}
            </label>
            <select
              value={selectedAttribute?.id || ''}
              onChange={(e) => {
                const attr = attributes.find(a => a.id === e.target.value);
                setSelectedAttribute(attr || null);
                setAttributeValue('');
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                              <option value="">{t('abac.selectAttribute')}</option>
              {attributes.map((attr) => (
                <option key={attr.id} value={attr.id}>
                  {attr.attribute_name} ({attr.data_type})
                </option>
              ))}
            </select>
          </div>

          {selectedAttribute && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('abac.userAttributeAssignment.attributeValue')}
              </label>
              {selectedAttribute.data_type === 'DATE' ? (
                <input
                  type="date"
                  value={attributeValue}
                  onChange={(e) => setAttributeValue(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : selectedAttribute.data_type === 'BOOLEAN' ? (
                <select
                  value={attributeValue}
                  onChange={(e) => setAttributeValue(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select...</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              ) : selectedAttribute.data_type === 'NUMBER' ? (
                <input
                  type="number"
                  value={attributeValue}
                  onChange={(e) => setAttributeValue(e.target.value)}
                  placeholder={t('abac.userAttributeAssignment.enterAttributeValue', { attributeName: selectedAttribute.attribute_name })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <input
                  type="text"
                  value={attributeValue}
                  onChange={(e) => setAttributeValue(e.target.value)}
                  placeholder={t('abac.userAttributeAssignment.enterAttributeValue', { attributeName: selectedAttribute.attribute_name })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
              {selectedAttribute.description && (
                <p className="text-sm text-gray-500 mt-1">{selectedAttribute.description}</p>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowAssignmentModal(false);
                setSelectedUser(null);
                setSelectedAttribute(null);
                setAttributeValue('');
              }}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSaveAssignment}
              disabled={!selectedAttribute || !attributeValue.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('abac.userAttributeAssignment.assignAttribute')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserAttributeAssignmentPage;
